import React, { useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import RevenueService from '../../services/revenueService';
import { formatCurrency } from '../../utils/currencyUtils';
import { describeApiError } from '../../utils/stockErrorUtils';

const RevenueReportPanel = ({ storeId, from, to, currency }) => {
  const { t, i18n } = useTranslation();
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const language = (i18n.language || 'pt').slice(0, 2);

  const load = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await RevenueService.getReport(storeId, { from, to, language, pageIndex: 0, pageSize: 50 });
      setReport(data);
    } catch (requestError) {
      setError(describeApiError(requestError, t));
    } finally {
      setBusy(false);
    }
  };

  const download = async (kind) => {
    setBusy(true);
    setError('');
    try {
      const action = kind === 'pdf' ? RevenueService.downloadPdf : RevenueService.downloadCsv;
      await action(storeId, { from, to, language });
    } catch (requestError) {
      setError(describeApiError(requestError, t));
    } finally {
      setBusy(false);
    }
  };

  const rows = report?.rows || report?.Rows || [];
  const totals = report?.paymentTotals || report?.PaymentTotals || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Button color="inherit" onClick={load} disabled={busy}>{t('revenueReport.show')}</Button>
        <PrimaryActionButton onClick={() => download('pdf')} disabled={busy}>{t('revenueReport.downloadPdf')}</PrimaryActionButton>
        <Button color="inherit" onClick={() => download('csv')} disabled={busy}>{t('revenueReport.downloadCsv')}</Button>
      </Box>
      {error ? <Typography color="error" sx={{ mb: 2 }}>{error}</Typography> : null}
      {report ? (
        <>
          <Typography sx={{ mb: 1 }}>
            {report.storeName || report.StoreName}
            {' · '}
            {report.storeVatNumber || report.StoreVatNumber || '—'}
            {' · '}
            {t('revenueReport.sales', { count: report.saleCount ?? report.SaleCount ?? 0 })}
          </Typography>
          <Typography sx={{ mb: 2 }}>
            {t('revenueReport.totalReceived')}
            {' '}
            {formatCurrency(report.totalReceived ?? report.TotalReceived ?? 0, currency)}
            {' · '}
            {t('revenueReport.totalVat')}
            {' '}
            {formatCurrency(report.totalVat ?? report.TotalVat ?? 0, currency)}
            {totals.map((total) => ` · ${total.paymentMethod || total.PaymentMethod} ${formatCurrency(total.amount ?? total.Amount, currency)}`)}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['id', 'date', 'professional', 'customer', 'vat', 'services', 'products', 'total', 'tax', 'payment'].map((column) => (
                  <TableCell key={column}>{t(`revenueReport.columns.${column}`)}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={10}>{t('revenueReport.empty')}</TableCell></TableRow>
              ) : rows.map((row) => (
                <TableRow key={row.sourceKey || row.SourceKey || row.id}>
                  <TableCell>{row.id || row.Id}</TableCell>
                  <TableCell>{row.date || row.Date}</TableCell>
                  <TableCell>{row.professional || row.Professional}</TableCell>
                  <TableCell>{row.customer || row.Customer}</TableCell>
                  <TableCell>{row.customerVatNumber || row.CustomerVatNumber}</TableCell>
                  <TableCell>{row.services || row.Services || row.description || row.Description}</TableCell>
                  <TableCell>{row.products || row.Products}</TableCell>
                  <TableCell>{formatCurrency(row.totalPaid ?? row.TotalPaid ?? 0, currency)}</TableCell>
                  <TableCell>{formatCurrency(row.vat ?? row.Vat ?? 0, currency)}</TableCell>
                  <TableCell>{row.payment || row.Payment}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      ) : null}
    </Box>
  );
};

export default RevenueReportPanel;
